import { motion } from "framer-motion";
import { Coins, Plus, TrendingUp, Check, Zap, Crown, ShoppingCart, AlertCircle, Clock, Calendar, Lock, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useCredits } from "@/hooks/useCredits";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const purchaseOptions = [
  {
    name: "Pacote Avulso",
    price: "R$ 27,90",
    description: "5 créditos (15 vídeos)",
    features: ["Sem validade", " Ideal para testar"],
    link: "https://pay.cakto.com.br/v2fgbt2",
    icon: Zap,
    color: "bg-blue-500",
    popular: false
  },
  {
    name: "Assinatura Mensal",
    price: "R$ 197,90",
    description: "30 créditos/mês",
    features: ["Renova todo mês", "R$ 6,59 por crédito"],
    link: "https://pay.cakto.com.br/985tku9_755701",
    icon: ShoppingCart,
    color: "bg-primary",
    popular: true
  },
  {
    name: "Assinatura Anual",
    price: "R$ 1.997,90",
    description: "360 créditos/ano",
    features: ["Renova todo ano", "R$ 5,54 por crédito"],
    link: "https://pay.cakto.com.br/pd4e923",
    icon: Crown,
    color: "bg-yellow-500",
    popular: false
  }
];

interface PurchaseRestriction {
  id: string;
  daily_credit_limit: number;
  restriction_end_date: string;
  credits_used: number;
  total_credits_allocated: number;
  restriction_start_date: string;
}

interface DailyUsage {
  usage_date: string;
  credits_used: number;
}

interface LockedCredit {
  id: string;
  amount: number;
  available_at: string;
  description: string;
  credit_type: string;
  expires_at: string | null;
}

export default function Creditos() {
  const {
    availableCredits,
    isLoading,
    subscriptionInfo
  } = useCredits();
  const [restrictions, setRestrictions] = useState<PurchaseRestriction[]>([]);
  const [todayUsage, setTodayUsage] = useState<number>(0);
  const [loadingRestrictions, setLoadingRestrictions] = useState(true);
  const [lockedCredits, setLockedCredits] = useState<LockedCredit[]>([]);
  const [loadingLockedCredits, setLoadingLockedCredits] = useState(true);

  useEffect(() => {
    loadRestrictions();
    loadLockedCredits();
  }, []);

  const loadRestrictions = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .single();

      if (!profile) return;

      // Load active restrictions
      const { data: restrictionsData } = await supabase
        .from('purchase_restrictions')
        .select('*')
        .eq('user_id', profile.id)
        .gte('restriction_end_date', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (restrictionsData) {
        setRestrictions(restrictionsData);

        // Load today's usage
        if (restrictionsData.length > 0) {
          const today = new Date().toISOString().split('T')[0];
          const { data: usageData } = await supabase
            .from('daily_credit_usage')
            .select('credits_used')
            .eq('user_id', profile.id)
            .eq('usage_date', today)
            .maybeSingle();

          if (usageData) {
            setTodayUsage(usageData.credits_used);
          }
        }
      }
    } catch (error) {
      console.error('Error loading restrictions:', error);
    } finally {
      setLoadingRestrictions(false);
    }
  };

  const loadLockedCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Query credit_balances for locked credits (available_at > now())
      const { data: lockedData, error } = await (supabase
        .from('credit_balances')
        .select('id, initial_amount, remaining_amount, available_at, description, credit_type, expires_at')
        .eq('user_id', user.id)
        .gt('available_at' as any, new Date().toISOString())
        .gt('remaining_amount', 0)
        .order('available_at' as any, { ascending: true }) as any) as { data: Array<{ id: string; remaining_amount: number; available_at: string | null; description: string | null; credit_type: string; expires_at: string | null }> | null; error: any };

      if (error) {
        console.error('Error loading locked credits:', error);
        return;
      }

      if (lockedData) {
        const formattedCredits: LockedCredit[] = lockedData.map(credit => ({
          id: credit.id,
          amount: credit.remaining_amount,
          available_at: credit.available_at || '',
          description: credit.description || 'Créditos bloqueados',
          credit_type: credit.credit_type,
          expires_at: credit.expires_at
        }));
        setLockedCredits(formattedCredits);
      }
    } catch (error) {
      console.error('Error loading locked credits:', error);
    } finally {
      setLoadingLockedCredits(false);
    }
  };

  const handleBuyCredits = (link: string, optionName: string) => {
    console.log(`[Creditos] Abrindo link de compra para: ${optionName}`);
    window.open(link, "_blank");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Créditos</h1>
          <p className="text-muted-foreground">
            Gerencie seus créditos para geração de vídeos
          </p>
        </div>

        {/* Active Subscription Alert */}
        {subscriptionInfo?.hasActiveSubscription && (
          <Alert className="border-green-200 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <div className="flex flex-col gap-2">
                <p className="font-medium">Assinatura Ativa</p>
                <p className="text-sm">
                  Você está no <strong>Plano {subscriptionInfo.currentPlan === 'mensal' ? 'Mensal' : 'Anual'}</strong>.
                  {subscriptionInfo.currentPlan === 'mensal' && ' Você pode fazer upgrade para o plano anual a qualquer momento.'}
                </p>
                {subscriptionInfo.subscriptionExpiresAt && (
                  <div className="flex items-center gap-1 text-xs mt-1">
                    <Calendar className="h-3 w-3" />
                    <span>Renova em: {new Date(subscriptionInfo.subscriptionExpiresAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Purchase Restriction Alert */}
        {!loadingRestrictions && restrictions.length > 0 && (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <div className="flex flex-col gap-2">
                <p className="font-medium">Restrição de Uso Ativa</p>
                <p className="text-sm">
                  Você pode usar até <strong>2 créditos por dia</strong> pelos próximos 7 dias após a compra.
                </p>
                <div className="flex items-center gap-4 text-xs mt-1">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Hoje: {todayUsage}/2 créditos usados</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Válido até: {new Date(restrictions[0].restriction_end_date).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }}>
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Saldo Atual
                    </p>
                    {isLoading ? <Skeleton className="h-10 w-16" /> : <p className="text-4xl font-bold text-gradient">
                      {availableCredits}
                    </p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      créditos disponíveis
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center">
                    <Coins className="w-7 h-7 text-primary-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }}>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Custo por Task
                    </p>
                    <p className="text-4xl font-bold">1</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      crédito por 3 vídeos gerados
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Purchase Options */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Comprar Créditos</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {purchaseOptions.map((option, index) => (
              <motion.div
                key={option.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + (index * 0.1) }}
              >
                <Card className={`relative h-full transition-all duration-300 hover:border-primary/50 hover:shadow-md ${option.popular ? 'border-primary shadow-sm' : ''}`}>
                  {option.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                        Mais Popular
                      </span>
                    </div>
                  )}
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center bg-secondary">
                      <option.icon className={`w-6 h-6 ${option.name === 'Pacote Avulso' ? 'text-blue-500' : option.name === 'Assinatura Mensal' ? 'text-primary' : 'text-yellow-500'}`} />
                    </div>
                    <CardTitle className="text-lg">{option.name}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="text-3xl font-bold">{option.price}</span>
                      {option.name.includes('Assinatura') && <span className="text-muted-foreground text-sm font-normal">/mês{option.name.includes('Anual') ? '*' : ''}</span>}
                    </div>

                    <ul className="space-y-2">
                      {option.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-muted-foreground">
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full font-semibold ${option.popular ? 'default' : 'secondary'}`}
                      onClick={() => handleBuyCredits(option.link, option.name)}
                      variant={option.popular ? "default" : "outline"}
                    >
                      Comprar Agora
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Locked Credits Section */}
        {!loadingLockedCredits && lockedCredits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-yellow-50 border-yellow-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-900">
                  <Lock className="h-5 w-5 text-yellow-600" />
                  Créditos Bloqueados
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-yellow-600" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Créditos de indicação ficam bloqueados por 7 dias após a compra do indicado
                          para garantir a segurança do sistema.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
                <CardDescription className="text-yellow-700">
                  Estes créditos estarão disponíveis em breve
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-yellow-700">
                      {lockedCredits.reduce((sum, credit) => sum + credit.amount, 0)} créditos
                    </span>
                  </div>

                  <div className="space-y-2">
                    {lockedCredits.map((credit) => (
                      <div key={credit.id} className="flex justify-between text-sm border-t border-yellow-200 pt-2">
                        <div>
                          <p className="font-medium text-yellow-900">{credit.description}</p>
                          <p className="text-yellow-600 text-xs">
                            {credit.credit_type === 'referral' && '🎁 Bônus de indicação'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-yellow-700">+{credit.amount}</p>
                          <p className="text-xs text-yellow-600 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(credit.available_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}



        {/* Info Card */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-3">Como funcionam os créditos?</h3>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Cada task de geração de vídeo consome 1 crédito
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Os créditos são debitados no momento da criação da task
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <strong>Ordem de consumo:</strong> Primeiro créditos comprados, depois créditos de assinatura, por último créditos de indicação
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Créditos de indicação ficam bloqueados por 7 dias após a compra do indicado
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Após a compra, você tem 7 dias para usar até 2 créditos por dia
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Você tem 7 dias para solicitar reembolso após a compra
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Ao solicitar reembolso, todos os créditos restantes serão removidos e você retornará ao plano gratuito
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Refund Policy Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-900 text-lg">Política de Reembolso</CardTitle>
            <CardDescription className="text-blue-700">
              Garantia de satisfação de 7 dias
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-blue-800 text-sm">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Você pode solicitar reembolso em até 7 dias após a compra</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>O reembolso é processado automaticamente via webhook</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Ao solicitar reembolso, você perderá todos os créditos restantes da compra e retornará ao plano gratuito</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Se você possui uma assinatura ativa, ela será cancelada automaticamente ao solicitar reembolso</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <span>Para solicitar reembolso, entre em contato com o suporte através do sistema de pagamento</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}